import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
} from 'react'

import { Center, Flex, Grid, GridItem, Text } from '@chakra-ui/react'
import { IAgoraRTCRemoteUser } from 'agora-rtc-react'
import { useVideoCallUsers } from 'src/features/videoCall/api/getVideoCallUsers'

import { isScreenShareUserUid } from '../../utils/screenShare'
import { VideoCallRoomRemotePlayer } from '../VideoCallRoomRemotePlayer/VideoCallRoomRemotePlayer'

type Props = {
  remoteUsers: IAgoraRTCRemoteUser[]
}

type Size = {
  width: number
  height: number
}

const getMaxCount = (
  containerSize: number,
  minGridItemSize: number,
  padding: number,
) => {
  let n = 0
  while (n * minGridItemSize + (n - 1) * padding <= containerSize) {
    n++
  }
  return n - 1
}

const gridItemMinHeight = 120

// 表示できるitemの数（itemが最小の高さで縦に表示できる個数 x itemが最小の幅で横に表示できる個数）を計算
const calculateMaxGridItemCount = (
  containerSize: Size,
  minGridItemWidth: number,
  padding: number,
) => {
  const widthCount = getMaxCount(containerSize.width, minGridItemWidth, padding)
  const heightCount = getMaxCount(
    containerSize.height,
    gridItemMinHeight,
    padding,
  )
  return widthCount * heightCount
}
const getGridItemMinWidth = (
  containerSize: Size,
  itemCount: number,
): number => {
  const isWideContainer = containerSize.width > 800
  const isMediumContainer = containerSize.width > 580
  const isFewItems = itemCount < 6

  const minWidth = isWideContainer
    ? isFewItems
      ? 400
      : 300
    : isFewItems
      ? 280
      : isMediumContainer
        ? 300
        : 160

  return minWidth
}

export const VideoCallRoomRemotePlayers: React.FC<Props> = ({
  remoteUsers,
}) => {
  const gridContainerRef = useRef<HTMLDivElement>(null)

  const subscribeContainerSize = useCallback((onStoreChange: () => void) => {
    const observer = new ResizeObserver(entries => {
      entries.forEach(() => onStoreChange())
    })
    if (gridContainerRef.current) {
      observer.observe(gridContainerRef.current)
    }
    return () => observer.disconnect()
  }, [])

  const containerHeight = useSyncExternalStore(subscribeContainerSize, () => {
    return gridContainerRef.current?.clientHeight ?? 0
  })
  const containerWidth = useSyncExternalStore(subscribeContainerSize, () => {
    return gridContainerRef.current?.clientWidth ?? 0
  })

  const containerSize = useMemo(() => {
    return {
      width: containerWidth,
      height: containerHeight,
    }
  }, [containerHeight, containerWidth])

  const { data: videoCallUsers, mutate } = useVideoCallUsers({
    videoCallUserUids: remoteUsers.map(user => user.uid as number),
  })

  // remoteUsersが変化したときにvideoCallUsersを再取得する
  useEffect(() => {
    mutate()
  }, [remoteUsers, mutate])

  const filteredRemoteUsers = remoteUsers.filter(
    user => !isScreenShareUserUid(user.uid),
  )

  const remoteUserCount = filteredRemoteUsers.length

  const gridItemMinWidth = getGridItemMinWidth(containerSize, remoteUserCount)

  const maxGridItemCount = useMemo(
    () =>
      calculateMaxGridItemCount(
        containerSize,
        gridItemMinWidth,
        remoteUserCount,
      ),
    [containerSize, gridItemMinWidth, remoteUserCount],
  )

  const maxShowableRemoteUserCount = useMemo(() => {
    return remoteUserCount <= maxGridItemCount
      ? remoteUserCount
      : Math.max(maxGridItemCount - 1, 0) // 0未満にならないようにする
  }, [remoteUserCount, maxGridItemCount])

  if (remoteUserCount === 0) {
    return (
      <Center boxSize="full" color="white">
        <Text fontSize="2xl" fontWeight="bold" color="white">
          あなた以外に参加者がいません
        </Text>
      </Center>
    )
  }

  return (
    <Flex
      direction="column"
      p="8px"
      boxSize="full"
      pb={remoteUserCount > 1 ? { base: '120px', lg: '196px' } : '8px'}
    >
      <Grid
        ref={gridContainerRef}
        w="full"
        h="full"
        gap="8px"
        templateColumns={`repeat(auto-fit, minmax(${gridItemMinWidth}px, 1fr))`}
        templateRows={`repeat(auto-fit, minmax(${gridItemMinHeight}px, 1fr))`}
      >
        {filteredRemoteUsers.slice(0, maxShowableRemoteUserCount).map(user => (
          <GridItem key={user.uid} minH={`${gridItemMinHeight}px`}>
            <VideoCallRoomRemotePlayer
              remoteUser={user}
              userName={
                videoCallUsers?.find(
                  videoCallUser =>
                    videoCallUser.videoCallUserUid === (user.uid as number),
                )?.name
              }
            />
          </GridItem>
        ))}

        {remoteUserCount > maxGridItemCount && (
          <GridItem>
            <Center bg="gray.100" boxSize="full">
              <Text fontSize="2xl" fontWeight="bold" color="white">
                他{remoteUserCount - maxShowableRemoteUserCount}人
              </Text>
            </Center>
          </GridItem>
        )}
      </Grid>
    </Flex>
  )
}
